<template>
  <div v-if="show" class="overlay">
    <div class="modal-card">
      <div class="modal-header">
        <div>
          <p class="page-kicker">Integration Access</p>
          <h3>Generate integration key</h3>
          <p>Select the account that should own this key.</p>
        </div>
        <button class="icon-button-soft" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="errorMessage" class="alert-message">
        <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
      </div>

      <div class="form-section">
        <label class="field-label">Account <span class="required">*</span></label>
        <input
          type="text"
          v-model="search"
          placeholder="Search users..."
          class="ui-input"
          @input="handleSearchInput"
          @focus="showDropdown = true"
        />

        <div class="dropdown" v-if="showDropdown && filteredUsers.length && search">
          <button
            type="button"
            v-for="user in filteredUsers"
            :key="user.uuid"
            class="dropdown-item"
            @click="selectUser(user)"
          >
            <span class="table-primary">{{ user.full_name }}</span>
            <span class="table-secondary">{{ user.clinic_name || 'No clinic assigned' }}</span>
          </button>
        </div>
      </div>

      <div class="form-section">
        <label class="field-label">Description</label>
        <textarea
          v-model="description"
          placeholder="Optional context for this key"
          class="ui-textarea"
          rows="3"
        ></textarea>
      </div>

      <div class="ui-actions">
        <button class="ui-button secondary" @click="$emit('close')">Cancel</button>
        <button class="ui-button primary" :disabled="!selectedUser || loading" @click="handleSubmit">
          <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-key'"></i>
          {{ loading ? 'Generating...' : 'Generate key' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllUser } from '@/services/userServices'
import { registerIntegration } from '@/services/integrationServices'

let cachedUsers = null

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: '',
      description: '',
      selectedUser: null,
      users: [],
      showDropdown: false,
      loading: false,
      errorMessage: ''
    }
  },
  computed: {
    filteredUsers() {
      if (!this.search) return []
      return this.users.filter(user =>
        String(user.full_name || '').toLowerCase().includes(this.search.toLowerCase()) ||
        String(user.clinic_name || '').toLowerCase().includes(this.search.toLowerCase())
      )
    }
  },
  mounted() {
    this.fetchUsers()
  },
  methods: {
    handleSearchInput() {
      if (this.search.length > 2 && !this.users.length) {
        this.fetchUsers()
      }
    },
    async fetchUsers() {
      try {
        if (Array.isArray(cachedUsers) && cachedUsers.length) {
          this.users = cachedUsers
          return
        }

        const response = await getAllUser()
        this.users = response.users || []
        cachedUsers = this.users
      } catch (error) {
        console.error('Error fetching users:', error)
      }
    },
    selectUser(user) {
      this.selectedUser = user
      this.search = user.full_name
      this.showDropdown = false
    },
    async handleSubmit() {
      try {
        this.loading = true
        this.errorMessage = ''

        if (!this.selectedUser?.uuid) {
          this.errorMessage = 'Please select an account.'
          return
        }

        await registerIntegration({
          user_uuid: this.selectedUser.uuid,
          description: this.description.trim() || null
        })

        this.$emit('close')
        this.$emit('success')
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to register integration key.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(8px);
  z-index: 9999;
}

.modal-card {
  width: min(560px, 100%);
  padding: 24px;
  border: 1px solid var(--ui-border);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
}

.modal-header h3 {
  margin: 0;
  color: var(--ui-text);
  font-size: 1.25rem;
  font-weight: 650;
}

.modal-header p:last-child {
  margin: 6px 0 0;
  color: var(--ui-muted);
}

.form-section + .form-section {
  margin-top: 16px;
}

.dropdown {
  margin-top: 8px;
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  background: #fff;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: var(--ui-shadow);
}

.dropdown-item {
  width: 100%;
  display: block;
  padding: 12px;
  border: 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  text-align: left;
}

.dropdown-item:hover {
  background: #f8fafc;
}
</style>
